import { useSafeAreaPadding } from '@/hooks/useSafeAreaPadding';
import { Ionicons } from '@expo/vector-icons';
import { useState } from 'react';
import { ScrollView } from 'react-native';
import {
  Adapt,
  Sheet,
  SizableText,
  Button as TamaguiButton,
  Select as TamaguiSelect,
  XStack,
} from 'tamagui';
import { Grabber } from '../Grabber';
import { MultiSelectOptionRow } from './MultiSelectOptionRow';
import type { MultiSelectProps } from './multiSelect.types';
import {
  getMultiSelectDisplayText,
  getSelectedOptions,
  toggleMultiSelectValue,
} from './multiSelect.utils';
import { Button } from '@components/Button/Button';
import { useAppTranslation } from '@hooks/useAppTranslation';

export function MultiSelect({
  options,
  values,
  onValuesChange,
  placeholder,
  snapPoints = undefined,
}: MultiSelectProps): React.ReactNode {
  const { t } = useAppTranslation();
  const padding = useSafeAreaPadding();
  const [open, setOpen] = useState(false);
  const selectedOptions = getSelectedOptions(options, values);
  const displayText = getMultiSelectDisplayText(selectedOptions, placeholder);
  const isPlaceholderVisible = selectedOptions.length === 0;

  const handleToggle = (value: string): void => {
    onValuesChange(toggleMultiSelectValue(values, value));
  };

  const clearAll = (): void => {
    onValuesChange([]);
  };

  return (
    <TamaguiSelect open={open} onOpenChange={setOpen} value={values[0] ?? ''}>
      <XStack gap="$2" alignItems="center">
        <TamaguiSelect.Trigger
          backgroundColor="$tertiary50"
          borderWidth={0}
          size="$3"
          borderRadius="$true"
          flex={1}
        >
          <XStack
            alignItems="center"
            justifyContent="space-between"
            width="100%"
          >
            <SizableText
              color={isPlaceholderVisible ? '$secondary75' : '$color'}
              numberOfLines={1}
            >
              {displayText}
            </SizableText>
            <Ionicons name="chevron-down" size={20} />
          </XStack>
        </TamaguiSelect.Trigger>
        {values.length > 0 && (
          <TamaguiButton
            backgroundColor="$tertiary50"
            borderRadius="$true"
            borderWidth={0}
            onPress={clearAll}
            padding="$2"
          >
            <Ionicons name="close-circle" size={20} />
          </TamaguiButton>
        )}
      </XStack>

      <Adapt when="sm" platform="touch">
        <Sheet
          modal
          dismissOnSnapToBottom
          dismissOnOverlayPress
          snapPointsMode={snapPoints ? 'percent' : 'fit'}
          snapPoints={snapPoints}
        >
          <Sheet.Frame
            paddingBottom={padding.bottomPadding}
            paddingTop="$true"
            borderRadius="$modal"
          >
            <Grabber />
            <ScrollView
              keyboardShouldPersistTaps="always"
              nestedScrollEnabled
              contentContainerStyle={{ flexGrow: 1 }}
            >
              <Adapt.Contents />
            </ScrollView>
          </Sheet.Frame>
          <Sheet.Overlay backgroundColor="$shadow" />
        </Sheet>
      </Adapt>

      <TamaguiSelect.Content zIndex={10000}>
        <TamaguiSelect.ScrollUpButton />
        <TamaguiSelect.Viewport
          minWidth={200}
          maxHeight={240}
          overflow="scroll"
          position="relative"
        >
          {options.map((option) => (
            <MultiSelectOptionRow
              key={option.value}
              option={option}
              selected={values.includes(option.value)}
              onPress={() => handleToggle(option.value)}
            />
          ))}
          <XStack position="sticky" zIndex={2} bottom={0}>
            <Button
              title={t('buttons.validate')}
              width="100%"
              onPress={() => setOpen(false)}
            />
          </XStack>
        </TamaguiSelect.Viewport>
        <TamaguiSelect.ScrollDownButton />
      </TamaguiSelect.Content>
    </TamaguiSelect>
  );
}
